import {Component, OnInit} from '@angular/core';
import {finalize} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {getOrderSourceLabel} from '../services/order-source-labels';
import {DataService} from '../services/data.service';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {TableSortState} from '../table-sort';

@Component({
    selector: 'app-orders',
    templateUrl: './orders.component.html',
    styleUrls: ['./orders.component.css']
})
export class OrdersComponent implements OnInit {
    orders: Array<any> = [];
    selectedOrder: any;
    phoneNumber = '';
    appliedPhoneNumber = '';
    carFilter = '';
    appliedCarFilter = '';
    statusFilter = '';
    appliedStatusFilter = '';
    sourceFilter = '';
    appliedSourceFilter = '';
    paymentMethodFilter = '';
    appliedPaymentMethodFilter = '';
    currentPage = 1;
    pageSize = 25;
    totalCount = 0;
    totalPages = 0;
    tableSort = new TableSortState();
    isLoading = false;
    loadError = '';

    constructor(
        private dataService: DataService,
        private restService: RestService,
        private uiBlockerService: UIBlockerService
    ) { /* noop */
    }

    ngOnInit() {
        this.loadOrders();
    }

    loadOrders() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier())) {
            return;
        }
        this.isLoading = true;
        this.loadError = '';
        this.restService.getOrders(
            this.currentPage,
            this.pageSize,
            this.appliedPhoneNumber,
            this.appliedCarFilter,
            this.appliedStatusFilter,
            this.appliedSourceFilter,
            this.appliedPaymentMethodFilter)
            .pipe(finalize(() => {
                this.isLoading = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe(
                (response: any) => this.applyOrdersResponse(response),
                () => {
                    this.orders = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                    this.loadError = 'The orders could not be loaded. Please try again.';
                }
            );
    }

    search() {
        this.appliedPhoneNumber = this.phoneNumber.trim();
        this.appliedCarFilter = this.carFilter.trim();
        this.appliedStatusFilter = this.statusFilter.trim();
        this.appliedSourceFilter = this.sourceFilter.trim();
        this.appliedPaymentMethodFilter = this.paymentMethodFilter.trim();
        this.currentPage = 1;
        this.loadOrders();
    }

    clearSearch() {
        if (this.phoneNumber === '' && this.appliedPhoneNumber === ''
            && this.carFilter === '' && this.appliedCarFilter === ''
            && this.statusFilter === '' && this.appliedStatusFilter === ''
            && this.sourceFilter === '' && this.appliedSourceFilter === ''
            && this.paymentMethodFilter === '' && this.appliedPaymentMethodFilter === '') {
            return;
        }
        this.phoneNumber = '';
        this.appliedPhoneNumber = '';
        this.carFilter = '';
        this.appliedCarFilter = '';
        this.statusFilter = '';
        this.appliedStatusFilter = '';
        this.sourceFilter = '';
        this.appliedSourceFilter = '';
        this.paymentMethodFilter = '';
        this.appliedPaymentMethodFilter = '';
        this.currentPage = 1;
        this.loadOrders();
    }

    hasAppliedFilters() {
        return this.appliedPhoneNumber !== ''
            || this.appliedCarFilter !== ''
            || this.appliedStatusFilter !== ''
            || this.appliedSourceFilter !== ''
            || this.appliedPaymentMethodFilter !== '';
    }

    changePageSize() {
        this.currentPage = 1;
        this.loadOrders();
    }

    goToPage(page: number) {
        if (page < 1 || page > this.totalPages || page === this.currentPage || this.isLoading) {
            return;
        }
        this.currentPage = page;
        this.loadOrders();
    }

    getVisiblePages() {
        const pages = [];
        const visibleCount = 5;
        let start = Math.max(1, this.currentPage - Math.floor(visibleCount / 2));
        const end = Math.min(this.totalPages, start + visibleCount - 1);
        start = Math.max(1, end - visibleCount + 1);
        for (let page = start; page <= end; page++) {
            pages.push(page);
        }
        return pages;
    }

    getFirstItemNumber() {
        return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    }

    getLastItemNumber() {
        return Math.min(this.currentPage * this.pageSize, this.totalCount);
    }

    getSortedOrders() {
        return this.tableSort.sort(this.orders, (order, column) => this.getOrderSortValue(order, column));
    }

    openDetails(order: any) {
        this.selectedOrder = order;
    }

    closeDetails() {
        this.selectedOrder = undefined;
    }

    getOrder(orderWrapper: any) {
        return isNullOrUndefined(orderWrapper) ? undefined : orderWrapper.Order || orderWrapper;
    }

    getRequest(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.InitialOrderRequest;
    }

    getCustomer(orderWrapper: any) {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.Customer;
    }

    getAssignedCar(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.AssignedCar;
    }

    getOrderStatus(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (!isNullOrUndefined(orderWrapper) && this.hasValue(orderWrapper.CloseReason)) {
            return orderWrapper.CloseReason;
        }
        return this.valueOrDash(isNullOrUndefined(order) ? undefined : order.OrderState);
    }

    getOrderSource(orderWrapper: any) {
        const request = this.getRequest(orderWrapper);
        return this.getSourceLabel(isNullOrUndefined(request) ? undefined : request.DevicePlatform);
    }

    getSourceLabel(source: any) {
        return getOrderSourceLabel(source);
    }

    getClientName(orderWrapper: any) {
        const customer = this.getCustomer(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(customer) ? undefined : customer.Name);
    }

    getClientPhone(orderWrapper: any) {
        const customer = this.getCustomer(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(customer) ? undefined : customer.PhoneNumber);
    }

    getPickupAddress(orderWrapper: any) {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.PickupAddress;
    }

    getEntryDate(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.EntryDate;
    }

    getCarDisplay(orderWrapper: any) {
        const car = this.getAssignedCar(orderWrapper);
        if (isNullOrUndefined(car)) {
            return '-';
        }
        const companyAndId = String(car.CompanyIdentifier || '') + String(car.CarId || '');
        return companyAndId !== '' ? companyAndId : this.valueOrDash(car.CarIdentifier);
    }

    getPrice(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        const value = Number(isNullOrUndefined(order) ? undefined : order.TaximeterTotalPrice);
        return isNaN(value) ? '-' : value.toFixed(2) + ' lei';
    }

    getPaymentMethod(orderWrapper: any) {
        return this.hasValue(isNullOrUndefined(orderWrapper) ? undefined : orderWrapper.PaymentMethodType)
            ? String(orderWrapper.PaymentMethodType).toLowerCase()
            : 'cash';
    }

    formatAddress(address: any) {
        if (isNullOrUndefined(address)) {
            return '-';
        }
        if (this.hasValue(address.CompleteAddress)) {
            return address.CompleteAddress;
        }
        const parts = [];
        if (this.hasValue(address.StreetName)) {
            parts.push(address.StreetName);
        }
        if (this.hasValue(address.StreetNumber)) {
            parts.push('Nr. ' + address.StreetNumber);
        }
        if (this.hasValue(address.BlocNumber)) {
            parts.push('Bl. ' + address.BlocNumber);
        }
        if (this.hasValue(address.ScaraNumber)) {
            parts.push('Sc. ' + address.ScaraNumber);
        }
        if (parts.length === 0 && this.hasValue(address.Locality)) {
            parts.push(address.Locality);
        }
        return parts.length === 0 ? '-' : parts.join(', ');
    }

    private getOrderSortValue(orderWrapper: any, column: string) {
        const order = this.getOrder(orderWrapper);
        switch (column) {
            case 'date':
                return this.getTimeValue(this.getEntryDate(orderWrapper));
            case 'client':
                return this.getClientName(orderWrapper);
            case 'source':
                return this.getOrderSource(orderWrapper);
            case 'phone':
                return this.getClientPhone(orderWrapper);
            case 'pickup':
                return this.formatAddress(this.getPickupAddress(orderWrapper));
            case 'car':
                return this.getCarDisplay(orderWrapper);
            case 'status':
                return this.getOrderStatus(orderWrapper);
            case 'price': {
                const price = Number(isNullOrUndefined(order) ? undefined : order.TaximeterTotalPrice);
                return isNaN(price) ? undefined : price;
            }
            case 'paymentMethod':
                return this.getPaymentMethod(orderWrapper);
            default:
                return '';
        }
    }

    private getTimeValue(value: any) {
        if (isNullOrUndefined(value) || value === '') {
            return undefined;
        }
        const time = new Date(value).getTime();
        return isNaN(time) ? undefined : time;
    }

    private applyOrdersResponse(response: any) {
        const parsedResponse = this.parseResponse(response);
        this.orders = !isNullOrUndefined(parsedResponse) && Array.isArray(parsedResponse.Items)
            ? parsedResponse.Items : [];
        this.totalCount = Number(parsedResponse && parsedResponse.TotalCount) || 0;
        this.totalPages = Number(parsedResponse && parsedResponse.TotalPages) || 0;
        if (this.totalPages > 0 && this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages;
            this.loadOrders();
        }
    }

    private parseResponse(response: any) {
        if (typeof response !== 'string') {
            return response;
        }
        try {
            return JSON.parse(response);
        } catch (error) {
            return {};
        }
    }

    private hasValue(value: any) {
        return !isNullOrUndefined(value) && String(value).trim() !== '';
    }

    private valueOrDash(value: any) {
        return this.hasValue(value) ? value : '-';
    }

}
